import { api, apiDelete, apiJson, ensureOk, API_PATH_ADMIN } from './client.js';

export function buildModelCredentialsPayload({ email, password, two_fa, is_test, accountDescription, timezone }) {
  const payload = {
    is_test,
    credentials: {
      email: email.trim(),
      password,
      two_fa: two_fa?.trim() || undefined,
    },
  };
  if (accountDescription !== undefined) payload.account_description = accountDescription;
  if (timezone !== undefined) payload.timezone = timezone;
  return payload;
}

export async function getModels() {
  return apiJson(`${API_PATH_ADMIN}/models`, {}, 'Failed to load models');
}

export async function createModelWorkspace(payload) {
  return apiJson(`${API_PATH_ADMIN}/models/workspace`,
    { method: 'POST', body: JSON.stringify(payload) }, 'Failed to create model workspace');
}

export async function createModelWithCredentials({ email, password, two_fa, is_test, accountDescription, timezone }) {
  return apiJson(
    `${API_PATH_ADMIN}/models`,
    { method: 'POST', body: JSON.stringify(buildModelCredentialsPayload({ email, password, two_fa, is_test, accountDescription, timezone })) },
    'Failed to create model',
  );
}

export async function getModel(id) {
  return apiJson(`${API_PATH_ADMIN}/models/${id}`, {}, 'Failed to load model');
}

export async function getModelBibleEntries(modelId) {
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/bible`, {}, 'Failed to load model bible');
}

export async function createModelBibleEntry(modelId, payload = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/bible`,
    {
      method: 'POST',
      body: JSON.stringify({
        title: payload.title,
        category: payload.category,
        content: payload.content,
        is_active: payload.isActive ?? payload.is_active ?? true,
        priority: Number(payload.priority || 0),
      }),
    },
    'Failed to create model bible fact',
  );
}

export async function updateModelBibleEntry(modelId, entryId, payload = {}) {
  const body = {};
  if (payload.title !== undefined) body.title = payload.title;
  if (payload.category !== undefined) body.category = payload.category;
  if (payload.content !== undefined) body.content = payload.content;
  if (payload.isActive !== undefined) body.is_active = payload.isActive;
  if (payload.is_active !== undefined) body.is_active = payload.is_active;
  if (payload.priority !== undefined) body.priority = Number(payload.priority || 0);
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/bible/${entryId}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'Failed to update model bible fact',
  );
}

export async function deleteModelBibleEntry(modelId, entryId) {
  await apiDelete(`${API_PATH_ADMIN}/models/${modelId}/bible/${entryId}`, 'Failed to delete model bible fact');
}

export async function importModelBibleEntries(modelId, payload = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/bible/import`,
    {
      method: 'POST',
      body: JSON.stringify({
        text: payload.text,
        category: payload.category,
        replace: Boolean(payload.replace),
      }),
    },
    'Failed to import model bible facts',
  );
}

export async function previewModelBibleFacts(modelId, payload = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/bible/preview`,
    {
      method: 'POST',
      body: JSON.stringify({
        query: payload.query,
        limit: payload.limit,
      }),
    },
    'Failed to preview model bible facts',
  );
}

export async function updateModel(id, { credentials }) {
  const body = {};
  if (credentials !== undefined) {
    body.credentials = { email: credentials.email.trim() };
    if (credentials.password != null && String(credentials.password).trim() !== '') {
      body.credentials.password = credentials.password;
    }
  }
  return apiJson(
    `${API_PATH_ADMIN}/models/${id}`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'Failed to update model',
  );
}

export async function deleteModel(id) {
  await apiDelete(`${API_PATH_ADMIN}/models/${id}`, 'Failed to delete model');
}

export async function getModelProfile(modelId) {
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/profile`, {}, 'Failed to load profile');
}

export async function getModelAccount(modelId) {
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/account`, {}, 'Failed to load account data');
}

export async function updateModelAccountDescription(
  modelId,
  {
    description,
    timezone,
    max_message_price,
    automation_mode,
    bot_paused,
    default_config_id,
    telegram_bot_token,
    telegram_chat_id,
    telegram_alerts_enabled,
    telegram_alert_events,
  },
) {
  const body = { description, timezone };
  if (max_message_price !== undefined) body.max_message_price = max_message_price;
  if (automation_mode !== undefined) body.automation_mode = automation_mode;
  if (bot_paused !== undefined) body.bot_paused = bot_paused;
  if (default_config_id !== undefined) body.default_config_id = default_config_id;
  if (telegram_bot_token !== undefined) body.telegram_bot_token = telegram_bot_token;
  if (telegram_chat_id !== undefined) body.telegram_chat_id = telegram_chat_id;
  if (telegram_alerts_enabled !== undefined) body.telegram_alerts_enabled = telegram_alerts_enabled;
  if (telegram_alert_events !== undefined) body.telegram_alert_events = telegram_alert_events;
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/account-description`,
    { method: 'PATCH', body: JSON.stringify(body) },
    'Failed to update account settings',
  );
}

export async function getTrafficSegmentation(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segmentation`,
    {},
    'Failed to load traffic segmentation',
  );
}

export async function updateTrafficSegmentation(modelId, enabled) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segmentation`,
    { method: 'PATCH', body: JSON.stringify({ enabled: Boolean(enabled) }) },
    'Failed to update traffic segmentation',
  );
}

export async function bootstrapTrafficSegments(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segmentation/bootstrap`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to create traffic segment presets',
  );
}

export async function createTrafficSegment(modelId, payload) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segments`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Failed to create traffic segment',
  );
}

export async function updateTrafficSegment(modelId, segmentId, payload) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segments/${segmentId}`,
    { method: 'PATCH', body: JSON.stringify(payload) },
    'Failed to update traffic segment',
  );
}

export async function deleteTrafficSegment(modelId, segmentId) {
  await apiDelete(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-segments/${segmentId}`,
    'Failed to delete traffic segment',
  );
}

export async function discoverTrafficSources(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-sources/discover`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to discover tracking links',
  );
}

export async function upsertTrafficSourceRule(modelId, payload) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-source-rules`,
    { method: 'POST', body: JSON.stringify(payload) },
    'Failed to map tracking link',
  );
}

export async function deleteTrafficSourceRule(modelId, ruleId) {
  await apiDelete(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-source-rules/${ruleId}`,
    'Failed to remove tracking-link mapping',
  );
}

export async function syncTrafficAttributions(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/traffic-sources/sync`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to sync fan traffic attribution',
  );
}

export async function getTimezones() {
  return apiJson(`${API_PATH_ADMIN}/timezones`, {}, 'Failed to load timezones');
}

export async function getModelProviders(modelId) {
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/providers`, {}, 'Failed to load providers');
}

export async function addModelProvider(modelId, { provider, external_id, credentials, priority = 0 }) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers`,
    { method: 'POST', body: JSON.stringify({ provider, external_id, credentials, priority }) },
    'Failed to add provider',
  );
}

export async function removeModelProvider(modelId, linkId) {
  await apiDelete(`${API_PATH_ADMIN}/models/${modelId}/providers/${linkId}`, 'Failed to remove provider');
}

export async function retryProviderConnection(modelId, linkId) {
  const res = await api(`${API_PATH_ADMIN}/models/${modelId}/providers/${linkId}/retry`, { method: 'POST' });
  await ensureOk(res, 'Failed to start reconnect');
}

export async function checkProviderConnectionHealth(modelId, linkId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/${linkId}/health-check`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to check provider health',
  );
}

export async function connectModelToOnlyfansapi(modelId, { proxy_country, force_connect, two_fa } = {}) {
  const body = {};
  if (proxy_country != null && proxy_country !== '') body.proxy_country = proxy_country;
  if (force_connect != null) body.force_connect = force_connect;
  if (two_fa != null && two_fa !== '') body.two_fa = two_fa;
  const res = await api(`${API_PATH_ADMIN}/models/${modelId}/providers/onlyfansapi/connect`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
  await ensureOk(res, 'Failed to start connection');
}

export async function connectModelOnlyfansApiAccount(
  modelId,
  { apiKey, accountId, connectionId, email, accountDescription, timezone } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/onlyfansapi/account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        account_id: accountId,
        connection_id: connectionId,
        email,
        account_description: accountDescription,
        timezone,
      }),
    },
    'Failed to connect OnlyFansAPI account',
  );
}

export async function connectModelOnlyMonsterAccount(
  modelId,
  { apiToken, accountId, platformAccountId, username, email, accountDescription, timezone } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/onlymonster/account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_token: apiToken,
        account_id: accountId,
        platform_account_id: platformAccountId,
        username,
        email,
        account_description: accountDescription,
        timezone,
      }),
    },
    'Failed to connect OnlyMonster account',
  );
}

export async function connectModelFanslyAccount(
  modelId,
  { apiKey, accountId, fanslyId, username, email, accountDescription, timezone } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        account_id: accountId,
        fansly_id: fanslyId,
        username,
        email,
        account_description: accountDescription,
        timezone,
      }),
    },
    'Failed to connect Fansly account',
  );
}

export async function listFanslyAuthAccounts(modelId, { apiKey } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/accounts`,
    {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey }),
    },
    'Failed to validate OnlyFansAPI key',
  );
}

export async function startFanslyAuthentication(
  modelId,
  { apiKey, username, password, name, proxyCountry, forceConnect = true } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/auth/start`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        username,
        password,
        name,
        proxy_country: proxyCountry || undefined,
        force_connect: Boolean(forceConnect),
      }),
    },
    'Failed to start Fansly authentication',
  );
}

export async function getFanslyAuthenticationStatus(modelId, { apiKey, accountId } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/auth/status`,
    {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey, account_id: accountId }),
    },
    'Failed to check Fansly authentication',
  );
}

export async function submitFanslyAuthenticationCode(modelId, { apiKey, accountId, code } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/auth/2fa`,
    {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey, account_id: accountId, code }),
    },
    'Failed to submit Fansly verification code',
  );
}

export async function reauthenticateFanslyAccount(modelId, { apiKey, accountId } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/auth/reauthenticate`,
    {
      method: 'POST',
      body: JSON.stringify({ api_key: apiKey, account_id: accountId }),
    },
    'Failed to re-authenticate Fansly account',
  );
}

export async function getFanslyDirectSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/direct-settings`,
    {},
    'Failed to load Fansly direct settings',
  );
}

export async function updateFanslyDirectSettings(
  modelId,
  { sessionToken, enabled, verifyNow = true, clearSession = false } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/direct-settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        session_token: sessionToken,
        enabled,
        verify_now: Boolean(verifyNow),
        clear_session: Boolean(clearSession),
      }),
    },
    'Failed to save Fansly direct settings',
  );
}

export async function connectModelFanltvOfapiAccount(
  modelId,
  { apiKey, accountId, connectionId, email, accountDescription, timezone } = {},
) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanltv-ofapi/account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        account_id: accountId,
        connection_id: connectionId,
        email,
        account_description: accountDescription,
        timezone,
      }),
    },
    'Failed to connect FanLTV OFAPI backup',
  );
}

export async function createModelOnlyfansApiAccount({
  apiKey,
  accountId,
  connectionId,
  email,
  accountDescription,
  timezone,
  isTest,
} = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/connect-onlyfansapi-account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        account_id: accountId,
        connection_id: connectionId,
        email,
        account_description: accountDescription,
        timezone,
        is_test: isTest,
      }),
    },
    'Failed to create OnlyFansAPI account model',
  );
}

export async function createModelFanslyAccount({
  apiKey,
  accountId,
  fanslyId,
  username,
  email,
  accountDescription,
  timezone,
  isTest,
} = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/connect-fansly-account`,
    {
      method: 'POST',
      body: JSON.stringify({
        api_key: apiKey,
        account_id: accountId,
        fansly_id: fanslyId,
        username,
        email,
        account_description: accountDescription,
        timezone,
        is_test: isTest,
      }),
    },
    'Failed to create Fansly account model',
  );
}

export async function cancelModelConnection(modelId, { provider } = {}) {
  const body = provider != null && provider !== '' ? { provider } : {};
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/cancel-connection`,
    { method: 'POST', body: JSON.stringify(body) },
    'Failed to cancel connection',
  );
}

export async function startFanvueOAuth(modelId, { includeInsights = false } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/oauth-start${includeInsights ? '?include_insights=true' : ''}`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to start Fanvue OAuth',
  );
}

export async function getFanvuePlatformOAuthSettings() {
  return apiJson(
    `${API_PATH_ADMIN}/providers/fanvue/platform-oauth-settings`,
    {},
    'Failed to load Fanvue platform app settings',
  );
}

export async function startFanvuePlatformOAuth(modelId, { includeInsights = false } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/platform-oauth-start${includeInsights ? '?include_insights=true' : ''}`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to start Fanvue platform app OAuth',
  );
}

export async function startFanvueSignupOAuth() {
  return apiJson(
    `${API_PATH_ADMIN}/providers/fanvue/signup-oauth-start`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to start Fanvue signup OAuth',
  );
}

export async function getFanvueOAuthSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/oauth-settings`,
    {},
    'Failed to load Fanvue OAuth settings',
  );
}

export async function updateFanvueOAuthSettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/oauth-settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        client_id: settings.clientId,
        client_secret: settings.clientSecret,
        meta_app_id: settings.metaAppId,
        meta_app_secret: settings.metaAppSecret,
        redirect_uri: settings.redirectUri,
        auth_base_url: settings.authBaseUrl,
        api_base_url: settings.apiBaseUrl,
        api_version: settings.apiVersion,
        oauth_scopes: settings.oauthScopes,
        webhook_secret: settings.webhookSecret,
      }),
    },
    'Failed to save Fanvue OAuth settings',
  );
}

export async function completeFanvueOAuth(modelId, { code, state, creatorUserUuid } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/oauth-complete`,
    {
      method: 'POST',
      body: JSON.stringify({ code, state, creator_user_uuid: creatorUserUuid }),
    },
    'Failed to complete Fanvue OAuth',
  );
}

export async function completeFanvueOAuthByState({ code, state, creatorUserUuid } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/providers/fanvue/oauth-complete`,
    {
      method: 'POST',
      body: JSON.stringify({ code, state, creator_user_uuid: creatorUserUuid }),
    },
    'Failed to complete Fanvue OAuth',
  );
}

export async function completeFanvueSignupOAuth({ code, state, creatorUserUuid } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/providers/fanvue/signup-oauth-complete`,
    {
      method: 'POST',
      body: JSON.stringify({ code, state, creator_user_uuid: creatorUserUuid }),
    },
    'Failed to complete Fanvue signup',
  );
}

export async function syncFanvueMessages(modelId, { chatUuids, sinceMessageUuid, limit, maxChats, enqueueIncoming } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/sync-messages`,
    {
      method: 'POST',
      body: JSON.stringify({
        chat_uuids: Array.isArray(chatUuids) && chatUuids.length ? chatUuids : undefined,
        since_message_uuid: sinceMessageUuid || undefined,
        limit,
        max_chats: maxChats,
        enqueue_incoming: Boolean(enqueueIncoming),
      }),
    },
    'Failed to sync Fanvue messages',
  );
}

export async function getFanvueFinancialStatus(modelId) {
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/financial-status`, {}, 'Failed to check purchase history access');
}

export async function syncFanvuePurchases(modelId, { startDate, endDate } = {}) {
  const query = new URLSearchParams({ max_pages: '20' });
  if (startDate) query.set('start_date', `${startDate}T00:00:00Z`);
  if (endDate) query.set('end_date', `${endDate}T00:00:00Z`);
  return apiJson(`${API_PATH_ADMIN}/models/${modelId}/providers/fanvue/sync-purchases?${query}`, { method: 'POST' }, 'Failed to import Fanvue purchases');
}

export async function syncFanslyMessages(modelId, { limit, maxChats, offset, search, order, before, enqueueUnanswered } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/fansly/sync-messages`,
    {
      method: 'POST',
      body: JSON.stringify({
        limit,
        max_chats: maxChats,
        offset,
        search: search || undefined,
        order: order || undefined,
        before: before || undefined,
        enqueue_unanswered: Boolean(enqueueUnanswered),
      }),
    },
    'Failed to sync Fansly messages',
  );
}

export async function getInstagramOAuthSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/instagram/oauth-settings`,
    {},
    'Failed to load Instagram OAuth settings',
  );
}

export async function updateInstagramOAuthSettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/instagram/oauth-settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        client_id: settings.clientId,
        client_secret: settings.clientSecret,
        redirect_uri: settings.redirectUri,
        auth_base_url: settings.authBaseUrl,
        token_url: settings.tokenUrl,
        graph_base_url: settings.graphBaseUrl,
        oauth_scopes: settings.oauthScopes,
      }),
    },
    'Failed to save Instagram OAuth settings',
  );
}

export async function startInstagramOAuth(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/instagram/oauth-start`,
    { method: 'POST', body: JSON.stringify({}) },
    'Failed to start Instagram OAuth',
  );
}

export async function completeInstagramOAuth(modelId, { code, state } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/instagram/oauth-complete`,
    {
      method: 'POST',
      body: JSON.stringify({ code, state }),
    },
    'Failed to complete Instagram OAuth',
  );
}

export async function startTelegramAccountLogin(modelId, { phone } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-account/start`,
    {
      method: 'POST',
      body: JSON.stringify({ phone }),
    },
    'Failed to start Telegram login',
  );
}

export async function getTelegramAccountAppSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-account/app-settings`,
    {},
    'Failed to load Telegram app settings',
  );
}

export async function updateTelegramAccountAppSettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-account/app-settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        api_id: settings.apiId,
        api_hash: settings.apiHash,
      }),
    },
    'Failed to save Telegram app settings',
  );
}

export async function getAigirlfactorySettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/aigirlfactory/settings`,
    {},
    'Failed to load AIGirlFactory settings',
  );
}

export async function updateAigirlfactorySettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/aigirlfactory/settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        persona_key: settings.personaKey,
        channel: settings.channel,
        api_token: settings.apiToken,
        generate_token: Boolean(settings.generateToken),
        require_signature: settings.requireSignature,
        is_active: settings.isActive,
      }),
    },
    'Failed to save AIGirlFactory settings',
  );
}

export async function getTelegramTributeSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-tribute/settings`,
    {},
    'Failed to load Telegram Tribute settings',
  );
}

export async function updateTelegramTributeSettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-tribute/settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        api_key: settings.apiKey,
        external_id: settings.externalId,
        is_active: settings.isActive,
        verify_now: Boolean(settings.verifyNow),
      }),
    },
    'Failed to save Telegram Tribute settings',
  );
}

export async function getTelegramBotPaymentsSettings(modelId) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-bot-payments/settings`,
    {},
    'Failed to load Telegram Bot Payments settings',
  );
}

export async function updateTelegramBotPaymentsSettings(modelId, settings = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-bot-payments/settings`,
    {
      method: 'PUT',
      body: JSON.stringify({
        bot_token: settings.botToken,
        business_connection_id: settings.businessConnectionId,
        is_active: settings.isActive,
        verify_now: Boolean(settings.verifyNow),
        set_webhook: Boolean(settings.setWebhook),
      }),
    },
    'Failed to save Telegram Bot Payments settings',
  );
}

export async function createTelegramBotPaymentInvoiceLink(modelId, payload = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-bot-payments/invoice-link`,
    {
      method: 'POST',
      body: JSON.stringify({
        title: payload.title,
        description: payload.description,
        star_count: Number(payload.starCount || payload.star_count || 0),
        payload: payload.payload,
        photo_url: payload.photoUrl,
        fan_id: payload.fanId,
        media_ids: payload.mediaIds,
      }),
    },
    'Failed to create Telegram invoice link',
  );
}

export async function completeTelegramAccountLogin(modelId, { phone, code, password } = {}) {
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-account/complete`,
    {
      method: 'POST',
      body: JSON.stringify({ phone, code, password }),
    },
    'Failed to complete Telegram login',
  );
}

export async function smokeSendTelegramAccount(modelId, { fanId, text, mediaIds } = {}) {
  const normalizedMediaIds = Array.isArray(mediaIds)
    ? mediaIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
    : [];
  return apiJson(
    `${API_PATH_ADMIN}/models/${modelId}/providers/telegram-account/smoke-send`,
    {
      method: 'POST',
      body: JSON.stringify({
        fan_id: Number(fanId),
        text,
        confirm_send: true,
        media_ids: normalizedMediaIds.length ? normalizedMediaIds : undefined,
      }),
    },
    'Failed to send Telegram smoke message',
  );
}
